/**
 * MessageThread.tsx — bilingual message history (Design Spec §04, ARCH §8.1).
 *
 * Every bubble carries two versions — English large + primary, German smaller
 * beneath a hairline divider, tagged `DE`. A caption under each bubble names the
 * direction explicitly so the record is never ambiguous at handoff:
 *   - inbound  → German is the fan's original; English is Maloum's translation → "wrote DE"
 *   - outbound → English is what the chatter wrote; German is what was sent → "sent DE"
 *
 * Rendering rules (ARCH §8.1 — incoming never-blank):
 *   - Inbound: show displayText (translated EN); if translationUnavailable show raw DE
 *     + a badge + retry. Never blank.
 *   - Bubbles use asymmetric corners (tail corner squared) to reinforce direction.
 *
 * D-09: times displayed in Europe/Berlin.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MessageDto, MessageMediaDto, PendingSendDto } from '../lib/api-client.js';
import { messagesApi } from '../lib/api-client.js';
import { mergeThreadStream } from '../lib/thread-stream.js';
import { formatTimeBerlin } from '../lib/time.js';
import { formatDuration, formatMinorAmount } from '../lib/format.js';
import { MediaLightbox } from './MediaLightbox.js';
import { Badge, Button } from '@crm/ui';

interface MessageThreadProps {
  /**
   * Ref to the thread's content root — ConversationPane attaches a ResizeObserver to it to
   * keep the viewport pinned to the newest message as bubbles/media grow. Presentational
   * only: MessageThread just forwards it to its root element.
   */
  contentRef?: React.Ref<HTMLDivElement>;
  messages: MessageDto[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  /**
   * In-flight/failed outbound sends (no materialized message row yet) — interleaved into
   * the thread by timestamp (createdAt), so a queued send lands at the bottom while a
   * 'sent'-but-unreconciled send sits at its real chronological position. Once the
   * reconciler materializes the real row the server drops the pending item and the real
   * bubble takes its place.
   */
  pendingSends?: PendingSendDto[];
  /** Load a failed send back into the composer for a fresh preview→confirm cycle. */
  onRetrySend?: (send: PendingSendDto) => void;
  /**
   * "Delete this message" (owner 2026-07-03). Deletes a DELIVERED outbound message — the
   * parent applies the CRM-local hide optimistically. Only wired for outbound messages.
   */
  onDeleteMessage?: (messageId: string) => void;
  /** "Delete this message" for a pending/failed outbound send (a pending bubble). */
  onDeleteSend?: (sendId: string) => void;
}

export function MessageThread({
  contentRef,
  messages,
  isLoading,
  hasMore,
  onLoadMore,
  pendingSends = [],
  onRetrySend,
  onDeleteMessage,
  onDeleteSend,
}: MessageThreadProps) {
  // Interleave delivered messages and in-flight/pending sends into ONE chronological
  // stream. Rendering the two lists back-to-back put a 'sent' send (ACKed but not yet
  // reconciled — routine on prod) below a newer delivered message, which read out of
  // order (chatter-reported 2026-07-13). A just-queued send still sorts last (createdAt
  // ≈ now), so nothing needs per-state special casing.
  //
  // Scroll behaviour lives in ConversationPane, which owns the scroll container and the
  // append/prepend data flow (open → snap to bottom; load-older → hold position;
  // new message → glide only when near the bottom). This stays purely presentational.
  const stream = React.useMemo(
    () => mergeThreadStream(messages, pendingSends),
    [messages, pendingSends],
  );

  if (isLoading && messages.length === 0 && pendingSends.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-text-muted text-sm">Loading messages…</span>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="flex flex-col gap-3 p-4">
      {hasMore && (
        <div className="flex justify-center py-1">
          <Button variant="ghost" size="sm" onClick={onLoadMore} loading={isLoading}>
            Load older messages
          </Button>
        </div>
      )}

      {stream.map((entry) =>
        entry.kind === 'message' ? (
          <MessageBubble
            key={`m:${entry.item.id}`}
            msg={entry.item}
            onDelete={entry.item.direction === 'outbound' ? onDeleteMessage : undefined}
          />
        ) : (
          <PendingSendBubble
            key={`p:${entry.item.sendId}`}
            send={entry.item}
            onRetry={onRetrySend}
            onDelete={onDeleteSend}
          />
        ),
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual message bubble
// ---------------------------------------------------------------------------

/**
 * MediaThumb — one media attachment, rendered from the EU media-proxy.
 *
 * The <img> src is a same-origin proxy path; the auth cookie rides along, and the
 * signed CDN URL never reaches the browser. Clicking opens the full-size lightbox.
 *
 * The operator is the creator, so locked PPV content is NOT hidden — we show the
 * poster un-blurred with a small "Not unlocked" corner chip (sale status), and the
 * lightbox reveals the full content. On error (expired signed URL, no poster, or
 * CSAM gate refusal) we degrade to a neutral, non-interactive placeholder.
 */
function MediaThumb({
  msg,
  media,
  locked,
  onOpen,
}: {
  msg: MessageDto;
  media: MessageMediaDto;
  locked: boolean;
  onOpen: () => void;
}) {
  // idle → (onError) → retrying → (onError again) → failed. A ONE-SHOT retry with a
  // cache-busting query recovers transient blips (CSAM-gate flap, cold HLS poster). A
  // stored-but-expired signed URL is refreshed server-side (the serve path enqueues a
  // thread-wide media re-walk) and shows on the next open — the retry never poll-hammers.
  type ThumbState = 'idle' | 'retrying' | 'failed';
  const [state, setState] = React.useState<ThumbState>('idle');
  const [bust, setBust] = React.useState(0);
  const retriedRef = React.useRef(false);
  const isVideo = media.type === 'video';
  const src =
    `/api/conversations/${msg.conversationId}/messages/${msg.id}/media/${media.uploadId}/thumb` +
    (bust ? `?r=${bust}` : '');

  const handleError = () => {
    // Nothing upstream to refresh, or already retried once → terminal.
    if (!media.hasThumbnail || retriedRef.current) {
      setState('failed');
      return;
    }
    retriedRef.current = true; // one-shot — no loop, no per-thumb storm
    setState('retrying');
    window.setTimeout(() => setBust((n) => n + 1), 4000);
  };

  // A thumbnail exists upstream but the proxy fetch failed (expired signed URL,
  // or the CSAM gate failing closed) — the media returns once the account's
  // worker reconnects and refreshes the signed URLs.
  if (state === 'retrying') {
    return (
      <div
        role="status"
        className="flex flex-col items-center justify-center w-40 h-40 rounded-lg bg-bg-hover border border-border text-text-subtle text-xs gap-1.5 px-3 text-center"
      >
        <span className="animate-pulse" aria-hidden="true">{isVideo ? '▶' : '📎'}</span>
        <span className="animate-pulse">Refreshing…</span>
      </div>
    );
  }

  if (state === 'failed' && media.hasThumbnail) {
    return (
      <div className="flex flex-col items-center justify-center w-40 h-40 rounded-lg bg-bg-hover border border-border text-text-subtle text-xs gap-1.5 px-3 text-center">
        <span aria-hidden="true">{isVideo ? '▶' : '📎'}</span>
        <span>Media unavailable — refreshing, check back shortly</span>
      </div>
    );
  }

  if (state === 'failed' || !media.hasThumbnail) {
    return (
      <div className="flex items-center justify-center w-40 h-40 rounded-lg bg-bg-hover border border-border text-text-subtle text-xs gap-1.5">
        <span aria-hidden="true">📎</span>
        <span>{isVideo ? 'Video' : 'Media'} unavailable</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={isVideo ? 'Play video' : 'View image full size'}
      className="group relative block w-40 max-w-full overflow-hidden rounded-lg border border-border bg-bg-hover cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg"
    >
      <img
        src={src}
        alt={isVideo ? 'Video preview' : 'Image'}
        loading="lazy"
        onError={handleError}
        onLoad={() => setState('idle')}
        className="block w-full h-auto object-cover transition-transform group-hover:scale-[1.02]"
      />
      {/* Operator can always play video, including locked PPV. */}
      {isVideo && (
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-scrim text-accent-fg text-sm">
            ▶
          </span>
        </span>
      )}
      {/* Locked PPV: subtle corner chip — content stays visible to the operator. */}
      {locked && (
        <span className="absolute top-1 left-1 flex items-center gap-1 rounded bg-scrim px-1.5 py-0.5 text-[10px] font-semibold text-accent-fg pointer-events-none">
          <span aria-hidden="true">🔒</span> Not unlocked
        </span>
      )}
      {isVideo && media.durationSec != null && (
        <span className="absolute bottom-1 right-1 text-[10px] font-mono text-accent-fg bg-scrim rounded px-1 leading-none py-0.5">
          {formatDuration(media.durationSec)}
        </span>
      )}
      {/* Hover affordance */}
      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
    </button>
  );
}

function DeTag() {
  return (
    <span
      aria-label="German"
      className="inline-flex items-center text-[10px] font-mono font-semibold text-text-subtle border border-border rounded px-1 py-px leading-none shrink-0 mt-px"
    >
      DE
    </span>
  );
}

/**
 * DeleteAffordance — Maloum-style "Delete this message" control for an outgoing bubble.
 *
 * Renders a trash icon that stays hidden until the bubble row is hovered/focused (group-hover),
 * then a two-step inline confirm so a delete is never one stray click. onConfirm fires the
 * parent mutation; the parent removes the bubble optimistically, so this control unmounts with
 * it (no busy state needed). Keyboard-accessible: the trash button is focusable and the confirm
 * buttons are real <button>s.
 */
function DeleteAffordance({ onConfirm }: { onConfirm: () => void }) {
  const [confirming, setConfirming] = React.useState(false);

  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1.5" role="group" aria-label="Confirm delete">
        <button
          type="button"
          onClick={onConfirm}
          className="text-[11px] font-medium text-danger hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger rounded px-0.5"
        >
          Delete this message
        </button>
        <span aria-hidden="true" className="text-text-subtle">·</span>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-[11px] text-text-subtle hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded px-0.5"
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      aria-label="Delete this message"
      title="Delete this message"
      className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-text-subtle hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger rounded leading-none"
    >
      <TrashIcon />
    </button>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="inline-block align-[-1px]"
    >
      <path d="M2.5 4h11M6 4V2.5h4V4M5 4l.5 9h5l.5-9M6.5 6.5v4M9.5 6.5v4" />
    </svg>
  );
}

function MessageBubble({
  msg,
  onDelete,
}: {
  msg: MessageDto;
  /** "Delete this message" — only passed for outbound messages. */
  onDelete?: (messageId: string) => void;
}) {
  const isOutbound = msg.direction === 'outbound';
  const queryClient = useQueryClient();

  // Show the German version beneath the English when we have both and they differ.
  const showDe =
    !msg.translationUnavailable &&
    !!msg.displayText &&
    !!msg.originalText &&
    msg.originalText !== msg.displayText;

  // 'stale' = a retranslate is queued server-side; the thread poll (2s while any
  // 'stale' row is visible — manual-only translation, owner 2026-07-03) picks up
  // the result automatically.
  const translating = msg.translationStatus === 'stale';

  // Per-click "kick" (owner 2026-07-13): the retranslate job finishes async (~1-3s)
  // with no WS completion push, so schedule one extra refetch ~1.5s after the click to
  // reveal the result ahead of the next poll tick. Tracked in a ref and cleared on
  // unmount so a bubble that scrolls out of the list leaves no stray timer firing.
  const kickTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (kickTimer.current) clearTimeout(kickTimer.current);
    },
    [],
  );

  const retranslateMut = useMutation({
    mutationFn: () => messagesApi.retranslate(msg.id),
    // Refetch so the row flips to 'stale' server-side state and the poll keeps going,
    // then kick one more refetch shortly after to catch the (usually ~1-3s) result
    // ahead of the next poll tick.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['messages', msg.conversationId] });
      if (kickTimer.current) clearTimeout(kickTimer.current);
      kickTimer.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['messages', msg.conversationId] });
      }, 1500);
    },
  });

  // German was involved when we show a DE secondary OR a raw-DE fallback (either
  // direction, translation unavailable). Name the direction in the caption (§04).
  const deInvolved = showDe || msg.translationUnavailable;

  // Caption language tag (auto-detected, owner 2026-07-12). Prefer the message's real
  // detected language so an English fan reads "wrote EN" instead of the wrong "wrote DE";
  // when the language is undetermined, fall back to the legacy "DE" note only if German
  // was actually involved (a DE secondary or raw-DE unavailable fallback was shown).
  const langLabel =
    msg.originalLang === 'en'
      ? 'EN'
      : msg.originalLang === 'de'
        ? 'DE'
        : deInvolved
          ? 'DE'
          : null;

  const bodyText = msg.displayText ?? msg.originalText;
  const hasMedia = msg.media.length > 0;
  // Locked PPV = the fan hasn't bought yet. The operator (creator) still sees the
  // full content; this only drives the "Not unlocked" status chip + caption.
  const locked = msg.isPpv && !msg.ppvUnlocked;

  // Lightbox: index of the media item being viewed full-size, or null (closed).
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);
  const ppvInfo = msg.isPpv
    ? {
        priceLabel:
          msg.priceAmountMinor != null
            ? formatMinorAmount(msg.priceAmountMinor, msg.priceCurrency ?? 'EUR')
            : null,
        locked,
      }
    : undefined;

  return (
    <>
    <div
      className={[
        'group flex flex-col gap-1 max-w-[78%]',
        isOutbound ? 'self-end items-end' : 'self-start items-start',
      ].join(' ')}
    >
      {/* Bubble — asymmetric corners reinforce direction (tail corner squared) */}
      <div
        className={[
          'px-3.5 py-2.5 text-sm rounded-2xl',
          isOutbound
            ? 'bg-accent/15 text-text border border-accent/30 rounded-br-md'
            : msg.translationUnavailable
              ? 'bg-warning/10 text-text border border-warning/30 rounded-bl-md'
              : 'bg-bg-card text-text border border-border rounded-bl-md',
        ].join(' ')}
      >
        {/* Media (media / chat_product) — rendered via the EU media-proxy */}
        {hasMedia && (
          <div className={['flex flex-wrap gap-2', bodyText ? 'mb-2' : ''].join(' ')}>
            {msg.media.map((m, i) => (
              <MediaThumb
                key={m.uploadId}
                msg={msg}
                media={m}
                locked={locked}
                onOpen={() => setLightboxIndex(i)}
              />
            ))}
          </div>
        )}

        {/* English — primary, never blank for text messages (ARCH §8.1) */}
        {bodyText ? (
          <p className="whitespace-pre-wrap break-words leading-snug">{bodyText}</p>
        ) : !hasMedia ? (
          <p className="italic text-text-subtle leading-snug">(empty)</p>
        ) : null}

        {/* Untranslated (both directions — owner 2026-07-02): the primary text above is
            the raw German; offer on-demand translation. 'failed' gets a badge + Retry,
            'pending' a plain Translate button, 'stale' a translating indicator (the
            thread poll picks up the result). */}
        {msg.translationUnavailable && (
          <div
            className={[
              'mt-2 flex items-center gap-2 border-t pt-2',
              isOutbound ? 'border-accent/25' : 'border-warning/30',
            ].join(' ')}
          >
            {translating || retranslateMut.isPending ? (
              <span className="text-xs text-text-muted animate-pulse">Translating…</span>
            ) : (
              // Translation is available to chatters AND admins (owner 2026-07-19): unlike
              // send/mute/delete, re-running our own DE⇄EN translation touches nothing on
              // Maloum and never appears as a chatter viewer — it only refreshes the CRM's
              // display text. The retranslate endpoint already admits admins (role bypass).
              <>
                {msg.translationStatus === 'failed' && (
                  <Badge variant="warning" className="text-xs">
                    Translation failed
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                  onClick={() => retranslateMut.mutate()}
                  loading={retranslateMut.isPending}
                >
                  {msg.translationStatus === 'failed' ? 'Retry translation' : 'Translate'}
                </Button>
              </>
            )}
          </div>
        )}

        {/* German — secondary, under a hairline divider, tagged DE */}
        {showDe && (
          <div
            className={[
              'mt-2 pt-2 border-t flex items-start gap-1.5',
              isOutbound ? 'border-accent/25' : 'border-border',
            ].join(' ')}
          >
            <DeTag />
            <span lang="de" className="text-xs text-text-subtle leading-snug whitespace-pre-wrap break-words">
              {msg.originalText}
            </span>
          </div>
        )}
      </div>

      {/* PPV badges */}
      {msg.isPpv && (
        <div className="flex items-center gap-1">
          <Badge variant="success" className="text-xs">PPV</Badge>
          {msg.priceAmountMinor != null && (
            <span className="text-xs text-success font-mono">
              {formatMinorAmount(msg.priceAmountMinor, msg.priceCurrency ?? 'EUR')}
            </span>
          )}
          {!msg.ppvUnlocked && <Badge variant="warning" className="text-xs">Locked</Badge>}
        </div>
      )}

      {/* Caption — names the direction explicitly (Design Spec §04) + the delete affordance
          for outbound messages (Maloum-style "Delete this message"). CRM-sent messages show
          the operator's name (owner 2026-07-11) — retroactive via the outbound_send join;
          natively-authored outbound (no CRM send record) keeps the "You" fallback. */}
      <div className="flex items-center gap-2 px-1">
        <span className="text-[11px] text-text-subtle font-mono">
          {isOutbound ? (msg.sentByName ?? 'You') : 'Fan'} · {formatTimeBerlin(msg.sentAt)}
          {langLabel && <span> · {isOutbound ? 'sent' : 'wrote'} {langLabel}</span>}
          {msg.isBackfill && <span className="opacity-60"> · backfill</span>}
        </span>
        {isOutbound && onDelete && <DeleteAffordance onConfirm={() => onDelete(msg.id)} />}
      </div>
    </div>

      {hasMedia && lightboxIndex !== null && (
        <MediaLightbox
          items={msg.media}
          startIndex={lightboxIndex}
          srcFor={(uploadId, variant) =>
            `/api/conversations/${msg.conversationId}/messages/${msg.id}/media/${uploadId}/${variant}`
          }
          ppv={ppvInfo}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Pending outbound send bubble (queued / sending / sent-unreconciled / failed)
// ---------------------------------------------------------------------------

/**
 * Chip copy per send state. The failed label is deliberately reason-agnostic —
 * internal gate/env details (disarmed flags, CSAM guard, retry counts) are never
 * surfaced to chatters.
 */
const PENDING_STATE_CHIP: Record<
  PendingSendDto['state'],
  { label: string; variant: 'outline' | 'info' | 'success' | 'danger'; inFlight: boolean }
> = {
  confirmed: { label: 'Queued', variant: 'outline', inFlight: true },
  sending: { label: 'Sending…', variant: 'info', inFlight: true },
  sent: { label: 'Sent', variant: 'success', inFlight: false },
  send_failed: { label: 'Failed — not delivered', variant: 'danger', inFlight: false },
};

/**
 * An outgoing bubble for a send that has no real message row yet. Mirrors the
 * outbound MessageBubble layout (EN primary, DE secondary under a hairline) but
 * with a dashed border while in transit and danger tokens on failure, so a
 * pending send is never mistaken for delivered history.
 */
function PendingSendBubble({
  send,
  onRetry,
  onDelete,
}: {
  send: PendingSendDto;
  onRetry?: (send: PendingSendDto) => void;
  /** "Delete this message" — removes this pending/failed send from the thread. */
  onDelete?: (sendId: string) => void;
}) {
  const failed = send.state === 'send_failed';
  const chip = PENDING_STATE_CHIP[send.state];
  // German secondary only when it differs (English-mode sends carry EN in previewDe).
  const showDe = !!send.previewDe && send.previewDe !== send.originalEn;

  return (
    <div className="group flex flex-col gap-1 max-w-[78%] self-end items-end">
      <div
        className={[
          'px-3.5 py-2.5 text-sm rounded-2xl rounded-br-md',
          failed
            ? 'bg-danger/10 text-text border border-danger/40'
            : 'bg-accent/10 text-text border border-dashed border-accent/40',
        ].join(' ')}
      >
        {send.originalEn ? (
          <p className="whitespace-pre-wrap break-words leading-snug">{send.originalEn}</p>
        ) : (
          <p className="italic text-text-subtle leading-snug">
            {send.hasMedia ? 'Media message (no caption)' : '(empty)'}
          </p>
        )}

        {showDe && (
          <div className="mt-2 pt-2 border-t border-accent/25 flex items-start gap-1.5">
            <DeTag />
            <span lang="de" className="text-xs text-text-subtle leading-snug whitespace-pre-wrap break-words">
              {send.previewDe}
            </span>
          </div>
        )}
      </div>

      {/* PPV / media badges — same row pattern as delivered bubbles */}
      {(send.isPpv || send.hasMedia) && (
        <div className="flex items-center gap-1">
          {send.isPpv && <Badge variant="success" className="text-xs">PPV</Badge>}
          {send.isPpv && send.priceAmountMinor != null && (
            <span className="text-xs text-success font-mono">
              {formatMinorAmount(send.priceAmountMinor, send.priceCurrency ?? 'EUR')}
            </span>
          )}
          {send.hasMedia && (
            <Badge variant="outline" className="text-xs">
              {(send.mediaCount ?? 1)} media
            </Badge>
          )}
        </div>
      )}

      {/* Status chip + retry */}
      <div className="flex items-center gap-1.5">
        <Badge
          variant={chip.variant}
          className={['text-xs', chip.inFlight ? 'animate-pulse' : ''].join(' ')}
          role="status"
        >
          {chip.label}
        </Badge>
        {failed && onRetry && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            title="Load this message back into the composer to translate and send again"
            onClick={() => onRetry(send)}
          >
            Retry
          </Button>
        )}
      </div>

      {/* Why it failed (chatter-reported 2026-07-12): the reason is a plain, actionable
          string from the server (never internal gate/env detail). Some reasons — timeouts,
          2xx-without-ACK — deliberately warn against a blind Retry that could double-send. */}
      {failed && send.failureReason && (
        <p className="text-xs text-danger/90 max-w-[36ch] text-right leading-snug px-1" role="status">
          {send.failureReason}
        </p>
      )}

      <div className="flex items-center gap-2 px-1">
        <span className="text-[11px] text-text-subtle font-mono">
          {/* Pending bubbles are conversation-scoped — a colleague's queued send shows
              THEIR name, not "You" (chatter attribution, owner 2026-07-11). */}
          {send.sentByName ?? 'You'} · {formatTimeBerlin(send.createdAt)}
        </span>
        {onDelete && <DeleteAffordance onConfirm={() => onDelete(send.sendId)} />}
      </div>
    </div>
  );
}
